export type AudioRecordingSession = {
  mimeType: string;
  stop: () => Promise<Blob>;
  cancel: () => void;
};

export type AudioRecordingOptions = {
  onLevel?: (level: number) => void;
};

const preferredMimeTypes = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export async function startAudioRecording(options: AudioRecordingOptions = {}): Promise<AudioRecordingSession> {
  if (!window.isSecureContext) {
    throw new Error('Microphone access requires HTTPS or localhost.');
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser does not provide microphone access.');
  }
  if (!('MediaRecorder' in window)) {
    throw new Error('This browser cannot record audio.');
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
  } catch (error) {
    throw new Error(microphoneErrorMessage(error));
  }

  const mimeType = preferredMimeTypes.find((candidate) => MediaRecorder.isTypeSupported(candidate));
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  } catch (error) {
    stream.getTracks().forEach((track) => track.stop());
    throw new Error(error instanceof Error ? error.message : 'The browser could not create an audio recorder.');
  }
  const chunks: Blob[] = [];
  let stopped = false;
  let resolveStopped!: (blob: Blob) => void;
  let rejectStopped!: (error: Error) => void;
  const stoppedPromise = new Promise<Blob>((resolve, reject) => {
    resolveStopped = resolve;
    rejectStopped = reject;
  });

  const meter = startLevelMeter(stream, options.onLevel);
  const stopTracks = () => {
    meter.stop();
    stream.getTracks().forEach((track) => track.stop());
  };
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });
  recorder.addEventListener('stop', () => {
    stopped = true;
    stopTracks();
    resolveStopped(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }));
  });
  recorder.addEventListener('error', (event) => {
    stopped = true;
    stopTracks();
    rejectStopped(new Error(event.error?.message || 'The browser could not record audio.'));
  });
  try {
    recorder.start(250);
  } catch (error) {
    stopTracks();
    throw new Error(error instanceof Error ? error.message : 'The browser could not start audio recording.');
  }

  return {
    mimeType: recorder.mimeType || mimeType || 'audio/webm',
    stop: async () => {
      if (!stopped && recorder.state !== 'inactive') recorder.stop();
      return stoppedPromise;
    },
    cancel: () => {
      if (!stopped && recorder.state !== 'inactive') recorder.stop();
      else stopTracks();
    },
  };
}

function startLevelMeter(stream: MediaStream, onLevel?: (level: number) => void) {
  if (!onLevel) return { stop: () => undefined };
  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass) return { stop: () => undefined };
  const context = new AudioContextClass();
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  const timer = window.setInterval(() => {
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let index = 0; index < samples.length; index += 1) sum += samples[index] * samples[index];
    onLevel(Math.min(1, Math.sqrt(sum / samples.length) * 3));
  }, 80);
  return {
    stop: () => {
      window.clearInterval(timer);
      void context.close().catch(() => undefined);
    },
  };
}

export function microphoneErrorMessage(error: unknown) {
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone access is blocked. Allow microphone permission in the browser address bar and try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone was found. Connect or enable a microphone and try again.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'The microphone is busy in another app. Close the other app and try again.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'The microphone could not be started.';
}
